/**
 * Suno Manager Module
 * Full-size interface for Suno management combining
 * the Playlist Generator and Library Browser panels.
 *
 * This module handles:
 * - Full-size panel expand/collapse from sidebar panels
 * - Two-column layout (Generator + Library)
 * - Transition animations between modes
 *
 * Architecture: DOM Reparenting (not Cloning)
 * Instead of cloning panel content (which loses event listeners),
 * we move the actual .panel-content nodes into the modal via appendChild().
 * On close, we move them back. All listeners, element cache refs, and
 * render targets are preserved automatically.
 */

import { closeAllPanels, syncPlaylistVisibility } from './overlay-panels.js';
import { sunoManagerState } from './state.js';
import { reinitLibraryInfiniteScroll } from './library-browser.js';
import * as eventTracker from './analytics/event-tracker.js';

// Module state
let isInitialized = false;
let keyboardHandler = null;

// Original parent refs for restoring reparented content
let generatorOriginalParent = null;
let libraryOriginalParent = null;

// Pending "return to panel" timeout (cancelled on rapid re-open)
let returnPanelTimeoutId = null;

/**
 * Initialize the Suno Manager module
 */
export function initSunoManager() {
  if (isInitialized) {
    console.log('[SunoManager] Already initialized');
    return;
  }

  const modal = document.getElementById('sunoManagerModal');
  if (!modal) {
    console.error('[SunoManager] Modal element not found');
    return;
  }

  // Set up expand button handlers
  setupExpandButtons();

  // Set up close button handler
  const closeBtn = document.getElementById('sunoManagerCloseBtn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => closeSunoManager());
  }

  // Set up header collapse button (returns to source panel)
  const headerCollapseBtn = document.getElementById('sunoManagerCollapseBtn');
  if (headerCollapseBtn) {
    headerCollapseBtn.addEventListener('click', () => {
      // Return to the panel that was used to open the manager
      const returnPanel = sunoManagerState.sourcePanel || 'generator';
      closeSunoManager(returnPanel);
    });
  }

  // Set up collapse-to-sidebar buttons
  const collapseGeneratorBtn = document.getElementById('collapseToGeneratorBtn');
  const collapseLibraryBtn = document.getElementById('collapseToLibraryBtn');

  if (collapseGeneratorBtn) {
    collapseGeneratorBtn.addEventListener('click', () => closeSunoManager('generator'));
  }

  if (collapseLibraryBtn) {
    collapseLibraryBtn.addEventListener('click', () => closeSunoManager('library'));
  }

  // Handle click outside to close
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeSunoManager();
    }
  });

  // Track sidebar panel opens (generator or library)
  document.addEventListener('panelOpened', (e) => {
    const panel = e.detail?.panel;
    if (panel === 'generator' || panel === 'library') {
      eventTracker.trackExtSunoPanelToggled({ action: 'opened' });
    }
  });

  // Close modal when playlist generation starts — the generate button
  // is the actual DOM node (reparented, not cloned), so its click handler
  // fires directly. This event lets us close the modal and open the
  // generator sidebar panel so the progress UI is visible.
  document.addEventListener('playlistGenerationStarted', () => {
    if (sunoManagerState.isOpen) {
      closeSunoManager('generator');
    }
  });

  isInitialized = true;
  console.log('[SunoManager] Initialized');
}

/**
 * Set up expand buttons in sidebar panels
 */
function setupExpandButtons() {
  const expandGeneratorBtn = document.getElementById('expandGeneratorBtn');
  const expandLibraryBtn = document.getElementById('expandLibraryBtn');

  if (expandGeneratorBtn) {
    expandGeneratorBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openSunoManager('generator');
    });
  }

  if (expandLibraryBtn) {
    expandLibraryBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openSunoManager('library');
    });
  }
}

/**
 * Open the full-size Suno Manager
 * @param {string} sourcePanel - 'generator' or 'library' - which panel triggered the open
 */
export function openSunoManager(sourcePanel = 'generator') {
  const modal = document.getElementById('sunoManagerModal');
  if (!modal) return;

  // Validate source panel
  if (sourcePanel !== 'generator' && sourcePanel !== 'library') {
    console.warn('[SunoManager] Invalid source panel:', sourcePanel);
    sourcePanel = 'generator';
  }

  // Cancel any pending "return to panel" timeout from a recent close
  if (returnPanelTimeoutId) {
    clearTimeout(returnPanelTimeoutId);
    returnPanelTimeoutId = null;
  }

  // Close sidebar panels first
  closeAllPanels();

  // Move actual DOM nodes into modal (preserves all event listeners)
  moveContentToModal();

  // Update state
  sunoManagerState.isOpen = true;
  sunoManagerState.sourcePanel = sourcePanel;

  // Show modal with animation
  requestAnimationFrame(() => {
    modal.classList.add('open');

    // Render Lucide icons in reparented content
    if (window.lucide) {
      window.lucide.createIcons({ nodes: [modal] });
    }
  });

  // Set up keyboard handler
  keyboardHandler = handleKeydown;
  document.addEventListener('keydown', keyboardHandler);

  // Dispatch event
  document.dispatchEvent(new CustomEvent('sunoManagerOpened', {
    detail: { sourcePanel }
  }));

  // Notify library browser that its panel is now visible (triggers loadLibrary if needed).
  // When opened from sidebar expand, library is already loaded and onPanelOpen guards against reload.
  document.dispatchEvent(new CustomEvent('panelOpened', { detail: { panel: 'library' } }));

  eventTracker.trackExtSunoPanelToggled({ action: 'expanded' });

  console.log('[SunoManager] Opened from:', sourcePanel);
}

/**
 * Close the Suno Manager
 * @param {string|null} returnToPanel - Optional: 'generator' or 'library' to return to that sidebar panel
 */
export function closeSunoManager(returnToPanel = null) {
  const modal = document.getElementById('sunoManagerModal');
  if (!modal) return;

  // Validate return panel
  if (returnToPanel !== null && returnToPanel !== 'generator' && returnToPanel !== 'library') {
    console.warn('[SunoManager] Invalid return panel:', returnToPanel);
    returnToPanel = null;
  }

  // Hide modal
  modal.classList.remove('open');

  // Update state
  sunoManagerState.isOpen = false;
  sunoManagerState.sourcePanel = null;

  // Remove keyboard handler
  if (keyboardHandler) {
    document.removeEventListener('keydown', keyboardHandler);
    keyboardHandler = null;
  }

  // Move content back to sidebar panels immediately.
  // The modal is fading out via CSS transition, so the brief moment of
  // empty containers is imperceptible.
  moveContentBack();

  // Return to a specific sidebar panel after the close animation finishes
  if (returnToPanel) {
    returnPanelTimeoutId = setTimeout(() => {
      returnPanelTimeoutId = null;
      const panelBtn = document.querySelector(`.icon-btn[data-panel="${returnToPanel}"]`);
      if (panelBtn) {
        panelBtn.click();
      }
    }, 300); // Match CSS transition duration
  } else {
    // No return panel (e.g., opened directly from drop modal) —
    // restore drop modal if there's no playlist or live mode active
    syncPlaylistVisibility();
  }

  // Dispatch event
  document.dispatchEvent(new CustomEvent('sunoManagerClosed'));

  eventTracker.trackExtSunoPanelToggled({ action: 'closed' });

  console.log('[SunoManager] Closed');
}

/**
 * Handle keyboard events
 * @param {KeyboardEvent} e
 */
function handleKeydown(e) {
  if (e.key === 'Escape' && sunoManagerState.isOpen) {
    e.preventDefault();
    e.stopPropagation();
    closeSunoManager();
  }
}

/**
 * Move sidebar panel content into the modal.
 *
 * Uses appendChild() on existing DOM nodes, which MOVES them (not copies).
 * All event listeners, element cache refs, and render targets are preserved.
 */
function moveContentToModal() {
  const generatorContent = document.querySelector('#generatorPanel .panel-content');
  const libraryContent = document.querySelector('#libraryPanel .panel-content');
  const modalGenerator = document.getElementById('sunoManagerGeneratorContent');
  const modalLibrary = document.getElementById('sunoManagerLibraryContent');

  if (generatorContent && modalGenerator) {
    generatorOriginalParent = generatorContent.parentNode;
    modalGenerator.appendChild(generatorContent);
  }

  if (libraryContent && modalLibrary) {
    libraryOriginalParent = libraryContent.parentNode;
    modalLibrary.appendChild(libraryContent);
    // Recreate IntersectionObserver — its root (the scroll container) changed
    reinitLibraryInfiniteScroll();
  }
}

/**
 * Move content back from the modal to the original sidebar panels.
 * Called on modal close to restore sidebar functionality.
 */
function moveContentBack() {
  const generatorContent = document.querySelector('#sunoManagerGeneratorContent .panel-content');
  const libraryContent = document.querySelector('#sunoManagerLibraryContent .panel-content');

  if (generatorContent && generatorOriginalParent) {
    generatorOriginalParent.appendChild(generatorContent);
    generatorOriginalParent = null;
  }

  if (libraryContent && libraryOriginalParent) {
    libraryOriginalParent.appendChild(libraryContent);
    libraryOriginalParent = null;
    // Recreate IntersectionObserver in the sidebar scroll context
    reinitLibraryInfiniteScroll();
  }
}

/**
 * Check if Suno Manager is open
 * @returns {boolean}
 */
export function isSunoManagerOpen() {
  return sunoManagerState.isOpen;
}
